import React from "react";

// region Imports - Components
import LimitedChipsField from "@atoms/LimitedChipsField";
// endregion Imports - Components
// region Imports - External libraries
import { Checkbox, TextField } from "unform-material-ui";
import { Grid, CircularProgress, Box, Divider, Popper } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
// endregion Imports - External libraries
// region Imports - Languages
import { ManagementMessages, GlobalMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared / Utils
import { TpeAlertWithFunctionality } from "../../types";
// endregion Imports - Shared / Utils

interface UserPermissionsProps {
  t: (key: string) => string;
  modalType: "details" | "update" | "register";
  formRef: any;
  loadingOptionsPermissions: boolean;
  activeOptionsPermissions: TpeAlertWithFunctionality[];
  optionsPermissions: TpeAlertWithFunctionality[]; // sortedOptionsPermissions
  openOptionsPermissions: boolean;
  setOpenOptionsPermissions: (open: boolean) => void;
  setActiveOptionsPermissions: (permissions: TpeAlertWithFunctionality[]) => void;
  checkAllPermissions: boolean;
  setCheckAllPermissions: (check: boolean) => void;
  handleCheckAllPermissionsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  defineValueAccordingModalType: (detail: any, update: any, register: any) => any;
}
const UserPermissions: React.FC<UserPermissionsProps> = ({
  t,
  modalType,
  formRef,
  loadingOptionsPermissions,
  activeOptionsPermissions,
  optionsPermissions,
  openOptionsPermissions,
  setOpenOptionsPermissions,
  setActiveOptionsPermissions,
  checkAllPermissions,
  setCheckAllPermissions,
  handleCheckAllPermissionsChange,
  defineValueAccordingModalType
}) => {

  // region Components
  const PermissionsPopperComponent = React.useMemo(() => (param: React.ComponentProps<typeof Popper>) => (
    <Popper {...param} style={{ border: "1px solid rgba(149,157,165,0.2)" }}>
      <Box {...param} />
      <Divider />
      <Box
        style={{
          backgroundColor: "white",
          height: "45px",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap"
        }}
      >
        <Checkbox
          name="undefined"
          checked={checkAllPermissions}
          onChange={handleCheckAllPermissionsChange}
          id="check-all"
          onMouseDown={(event) => event.preventDefault()}
        />
        {t(GlobalMessages.selectAll)}
      </Box>
    </Popper>
  ), [checkAllPermissions, handleCheckAllPermissionsChange, t]);
  // endregion Components

  return (
    <Grid item xs={12} md={12} lg={12}>
      <Autocomplete
        multiple
        fullWidth
        disableCloseOnSelect
        renderTags={(value, getTagProps) => (
          <LimitedChipsField
            data={value}
            limit={2}
            getTagProps={getTagProps}
            labelsFormat={(permission: TpeAlertWithFunctionality) => permission.description ?? ""}
            keysFormat={(permission: TpeAlertWithFunctionality) => permission.id_alert_type || permission.idFunctionality || ""}
          />
        )}
        limitTags={2}
        value={activeOptionsPermissions}
        open={openOptionsPermissions}
        onOpen={() => setOpenOptionsPermissions(true)}
        onClose={() => setOpenOptionsPermissions(false)}
        onChange={(_event, newValue, reason) => {
          if (reason === "select-option") {
            setActiveOptionsPermissions(newValue);
          } else if (reason === "remove-option") {
            setCheckAllPermissions(false);
            setActiveOptionsPermissions(newValue);
          } else if (reason === "clear") {
            setCheckAllPermissions(false);
            setActiveOptionsPermissions([]);
          }
          // validations.validateFieldError("permissions"); // Handled by parent or context
          // For now assume parent handles validation triggers or we pass a callback,
          // but onChange updates state which triggers render, passing new value.
        }}
        getOptionLabel={(option) => option.description as string}
        getOptionSelected={(option, value) => option.description === value.description}
        options={optionsPermissions}
        disabled={modalType === "details"}
        loading={loadingOptionsPermissions}
        groupBy={(option) => (option.isAFunctionality
          ? t(ManagementMessages.functionalityGroup)
          : t(ManagementMessages.alertVisualizationGroup))}
        defaultValue={defineValueAccordingModalType(activeOptionsPermissions, activeOptionsPermissions, [])}
        PopperComponent={PermissionsPopperComponent}
        renderOption={(option, { selected }) => (
          <>
            <Checkbox
              name="undefined"
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              checked={selected || checkAllPermissions}
            />
            {option.description}
          </>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            name="permissions"
            label={t(ManagementMessages.detailsPermissions)}
            margin="dense"
            variant="outlined"
            InputLabelProps={{
              shrink: activeOptionsPermissions.length > 0
                || formRef.current?.getFieldValue("permissions")?.length > 0
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingOptionsPermissions ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </Grid>
  );
};

export default UserPermissions;
